import { createContext, useMemo, useContext } from "react";
import { ConversationGroup, ConversationSources } from "./types";
import ConversationGroupComponent from "../ConversationGroup";
import { User } from "../../../../user/types";
import { DbtDocsShareDetails } from "../../types";

interface Props {
  currentUser?: User;
  conversationGroup?: ConversationGroup;
  shareId?: DbtDocsShareDetails["share_id"];
  isSelected: boolean;
  users: Record<User["id"], User>;
  onSelect: () => void;
  onResolve: () => void;
  onReplyAdd: () => void;
  source: ConversationSources;
}

const ConversationGroupContext = createContext<Props>({
  users: {},
  conversationGroup: undefined,
  currentUser: undefined,
  isSelected: false,
  shareId: undefined,
  onSelect: () => null,
  onResolve: () => null,
  onReplyAdd: () => null,
  source: ConversationSources.DBT_DOCS,
});

const ConversationGroupProvider = ({
  currentUser,
  conversationGroup,
  shareId,
  onSelect,
  isSelected,
  users,
  onResolve,
  onReplyAdd,
  source,
}: Props): JSX.Element | null => {
  const values = useMemo(
    () => ({
      currentUser,
      conversationGroup,
      shareId,
      onSelect,
      isSelected,
      users,
      onResolve,
      onReplyAdd,
      source,
    }),
    [
      currentUser,
      conversationGroup,
      shareId,
      onSelect,
      isSelected,
      users,
      onResolve,
      onReplyAdd,
      source,
    ]
  );

  if (!conversationGroup || !shareId) {
    return null;
  }

  return (
    <ConversationGroupContext.Provider value={values}>
      <ConversationGroupComponent
        conversationGroup={conversationGroup}
        shareId={shareId}
        onSelect={onSelect}
      />
    </ConversationGroupContext.Provider>
  );
};

export default ConversationGroupProvider;

export const useConversationGroupContext = () => useContext(ConversationGroupContext);
